import { RNFile } from './requests'
import dayjs from 'dayjs'

export interface PagedResponse<T> {
  count: number
  next?: string
  currentPage: number
  totalPages: number
  previous?: string
  results: T[]
}

export type GENDER = 'MALE' | 'FEMALE'
export const CustomerStatuses = ['CANCELLED', 'ACTIVE', 'PENDING_CANCELLATION']

export type Profile = {
  avatar: string | null
  file?: RNFile
  date_of_birth: string | null
  auth_type?: 'SOCIAL' | 'EMAIL'
  is_staff: boolean
  first_name: string | null
  gender: GENDER | null
  id?: string
  last_name: string | null
}

export type ID = {
  id: string
}

export type Url = {
  url: string
}

export type CodePushProgress = {
  totalBytes: number
  receivedBytes: number
  percent: number
}

// END OF RESPONSE TYPES
export type ActiveSubscriptions = {
  total: number
  api: number
  campaign: number
}
export type Signups = {
  today: number
  yesterday: number
  this_month: number
  last_month: number
}
export type Stats = {
  active_subscriptions: ActiveSubscriptions
  sign_ups: Signups
}

export enum BillingPeriod {
  'MONTHLY' = 1,
  'ANNUAL' = 2,
}

export const getBillingPeriodDescription = (v: BillingPeriod | undefined) => {
  if (!v) {
    return null
  }
  if (v === 1) {
    return 'Monthly'
  } else {
    return 'Annual'
  }
}

export type Address = {
  address_line_1: string
  address_line_2: string | null
  address_line_3?: string | null
  postal_code: string
  city: string
  country: string
}

export type ShippingAddress = {
  shipping_address_line_1: string
  shipping_address_line_2: string | null
  shipping_address_line_3?: string | null
  shipping_address_postcode: string
  shipping_address_city: string
  shipping_address_country: string
}

export const convertAddressToShipping = (address: Address) => {
  return {
    shipping_address_city: address?.city || '',
    shipping_address_country: address?.country || '',
    shipping_address_line_1: address?.address_line_1 || '',
    shipping_address_line_2: address?.address_line_2 || '',
    shipping_address_line_3: address?.address_line_3 || '',
    shipping_address_postcode: address?.postal_code || '',
  } as ShippingAddress
}

export const convertShippingToAddress = (address: ShippingAddress) => {
  return {
    address_line_1: address?.shipping_address_line_1 || '',
    address_line_2: address?.shipping_address_line_2 || '',
    address_line_3: address?.shipping_address_line_3 || '',
    city: address?.shipping_address_city || '',
    country: address?.shipping_address_country || '',
    postal_code: address?.shipping_address_postcode || '',
  } as Address
}

export enum BusinessType {
  'LIMITED_COMPANY' = 'LIMITED_COMPANY',
  'SOLE_TRADER' = 'SOLE_TRADER',
  'CHARITY' = 'CHARITY',
  'UNINCORPORATED' = 'UNINCORPORATED',
  'TRUST' = 'TRUST',
  'ASSOCIATION' = 'ASSOCIATION',
}

import LocalizedStrings from 'react-localization'
import { Project } from 'common/project'

export const strings = new LocalizedStrings({
  en: {
    COMPANY_NUMBER: 'Company Number',
    LIMITED_COMPANY: 'Limited company (LTD, LP, LLP, LLC, Corp)',
  },
  'en-au': {
    COMPANY_NUMBER: 'ABN/ACN',
    LIMITED_COMPANY: 'Limited company (Pty Ltd, Ltd, LP)',
  },
})
if (Project.language) {
  strings.setLanguage('en-au')
}

export const businessTypeDescriptions: Record<BusinessType, string> = {
  ASSOCIATION: 'Association, club or society',
  CHARITY: 'Charity / non-profit',
  LIMITED_COMPANY: strings.LIMITED_COMPANY,
  SOLE_TRADER: 'Individual / sole trader',
  TRUST: 'Trust, foundation or fund',
  UNINCORPORATED: 'Unincorporated / not yet registered',
}
export type CustomerStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING_INACTIVATION'
export type Document = {
  file_name: string
  id: number
  reason_data?: {
    reason: string
  }
  document_type: 'ID' | 'PROOF_OF_ADDRESS' | 'AUDIT_REPORT'
  url: string
  status: PartnerAuditStatus
}
export type Member = {
  id?: number
  documents?: Document[]
  email: string | null
  first_name: string
  last_name: string
  date_of_birth: string
  phone_number?: string
  audit_report?: RNFile | null
  kyc_poa?: RNFile | null
  kyc_id?: RNFile | null
  'is_on_pep_list': KYCStatus
  'proof_of_address_status': KYCStatus
  'id_status': KYCStatus
}

export type VeriffStatusType =
  | 'NOT_STARTED'
  | 'STARTED'
  | 'EXPIRED'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'DECLINED'
  | 'CREATED'

export type VeriffStatus = {
  id: string
  status: VeriffStatusType
  reason: any
  created_at: string
  updated_at: string
}

export type MemberVeriff = {
  id: string
  dob: string
  first_name: string
  last_name: string
  is_email_notification: boolean
  middle_name?: string
  email: string
  phone_number: any
  created_at: string
  updated_at: string
  portal_link: string
  vid: VeriffStatus
  poa: VeriffStatus
  aml: VeriffStatus
}
export type CompanySubscriptionDetail = {
  id?: number
  company_id?: number
  location: Address
  subscription: { start_date: string; end_date?: string }
  company_name: string | undefined
  email_address: string | undefined
  groups: {
    group_id: number
    name: string
    id: number
  }[]
  trading_name?: string | undefined
  contact_first_name: string | undefined
  contact_last_name: string | undefined
  company_number?: string | undefined
  business_type: BusinessType
  external_id?: string
  telephone_number?: string
  created_at?: string
  plan_id: number
  status: 'NO_ID' | 'APPROVED' | 'CANCELLED' | 'ACTIVE' | 'PENDING_CANCELLATION'
  address: Address
  members: Member[]
  membersVeriff: MemberVeriff[]
  acn_number?: string
  abn_number?: string
  subscription_state: CustomerStatus //legacy
  subscription: {
    start_date: string
    end_date?: string
  }
}
export type Group = {
  id: number
  users: GroupMember[]
  name: string
  companies_count: number
}
export enum APIBusinessType {
  'LIMITED_COMPANY' = 1,
  'SOLE_TRADER' = 3,
  'CHARITY' = 10,
  'UNINCORPORATED' = 9,
  'TRUST' = 12,
  'ASSOCIATION' = 13,
}
function getEnumKeyByValue<T>(
  enumObject: T,
  value: T[keyof T],
): keyof T | undefined {
  return Object.keys(enumObject as any).find(
    (key) => enumObject[key as keyof T] === value,
  ) as keyof T | undefined
}

export type APICompanySubscriptionDetail = {
  subscription_id?: string
  external_id?: string
  'product_id': number
  'customer': {
    'first_name': string
    'last_name': string
    'email_address': string
  }
  'shipping_address': {
    'shipping_address_line_1': string
    'shipping_address_line_2': string | null
    'shipping_address_line_3': string | null
    'shipping_address_city': string
    'shipping_address_postcode': string
    'shipping_address_country': string
  }
  'subscription': {
    'start_date': string
    'end_date'?: string
  }
  status: CustomerStatus
  'company': {
    'name': string
    'trading_name': string
    'limited_company_number': string
    'organisation_type': APIBusinessType
    'telephone_number': string
    'abn_number': string
    'acn_number': string
  }
  'members': CompanySubscriptionDetail['members']
  'groups': {
    'id': number
    'name': string
    'group_id': number
  }[]
}

export function convertSubscriptionToAPI(value: CompanySubscriptionDetail) {
  return {
    'company': {
      'abn_number': value.abn_number,
      'acn_number': value.acn_number,
      'limited_company_number': value.company_number,
      'name': value.company_name,
      'organisation_type': APIBusinessType[value.business_type] as any,
      'telephone_number': value.telephone_number,
      'trading_name': value.trading_name,
    },
    'customer': {
      'email_address': value.email_address,
      'first_name': value.contact_first_name,
      'last_name': value.contact_last_name,
    },
    external_id: value.external_id || undefined,
    groups: value.groups,
    'members': value.members,
    'product_id': value.plan_id,
    'shipping_address': {
      'shipping_address_city': value.address.city,
      'shipping_address_country': value.address.country,
      'shipping_address_line_1': value.address.address_line_1,
      'shipping_address_line_2': value.address.address_line_2,
      'shipping_address_line_3': value.address.address_line_3,
      'shipping_address_postcode': value.address.postal_code,
    },
    status: 'ACTIVE',
    'subscription': {
      'start_date': value.created_at || dayjs().format('YYYY-MM-DD'),
    },
    subscription_id: value.id,
  } as APICompanySubscriptionDetail
}

export function convertAPISubscriptionToNormal(
  value: APICompanySubscriptionDetail,
) {
  const res = {
    ...value,
    abn_number: value.company.abn_number,
    acn_number: value.company.acn_number,
    address: {
      address_line_1: value.shipping_address.shipping_address_line_1,
      address_line_2: value.shipping_address.shipping_address_line_2,
      address_line_3: value.shipping_address.shipping_address_line_3,
      city: value.shipping_address.shipping_address_city,
      country: value.shipping_address.shipping_address_country,
      postal_code: value.shipping_address.shipping_address_postcode,
    },
    business_type: getEnumKeyByValue(
      APIBusinessType,
      value.company.organisation_type,
    ),
    company_name: value.company.name,
    company_number: value.company.limited_company_number,
    contact_first_name: value.customer.first_name,
    contact_last_name: value.customer.last_name,
    created_at: value.subscription.start_date,
    email_address: value.customer.email_address,
    external_id: value.external_id,
    groups: value.groups,
    id: value.subscription_id,
    members: (value.members || []).map((v) => {
      const kyc_id = v.documents?.find((v) => v.type === 'ID')
      const kyc_poa = v.documents?.find((v) => v.type === 'PROOF_OF_ADDRESS')
      const audit_report = v.documents?.find((v) => v.type === 'AUDIT_REPORT')
      return {
        ...v,
        audit_report,
        kyc_id,
        kyc_poa,
      }
    }),
    plan_id: value.product_id,
    status: value.status as any,
    subscription: {
      end_date: value.subscription.end_date,
      start_date: value.subscription.start_date,
    },
    telephone_number: value.company.telephone_number,
    trading_name: value.company.trading_name,
  } as CompanySubscriptionDetail
  return res
}

export type CompanySubscriptionSummary = {
  address_line_1: string
  address_line_2: string | null
  address_line_3?: string | null
  company_id: number
  postal_code: string
  city: string
  country: string
  id: number
  groups?: { id: number; group_id: number; name: string }[]
  external_id?: string | undefined
  company_name: string | undefined
  company_number?: string | undefined
  acn_number?: string | undefined
  abn_number?: string | undefined
  contact_name: string | undefined
  created_at: string
  email_address: string | undefined
  plan_id: number
  status: 'NO_ID' | 'APPROVED' | 'CANCELLED' | 'ACTIVE'
  telephone_number?: string
  trading_name?: string | undefined
  subscription_state: CustomerStatus
}

export type PartnerProduct = {
  'id': 1
  'name': string
  'description': string
  'cost': number
  requires_kyc: 0 | 1
  billing_period: BillingPeriod
}
export type RoleSummary = {
  id: number
  name: 'PARTNER_ADMIN' | 'USER_WRITE' | 'USER_READ'
  description: string
}

export type MailStatus =
  | 'ACCOUNT_FROZEN'
  | 'COLLECTED'
  | 'CREATED'
  | 'DESTROYED'
  | 'FORWARDED'
  | 'ON_HOLD_INVOICE'
  | 'ON_HOLD_KYC_ID'
  | 'PENDING'
  | 'PROCESSING'
  | 'REFUSED'
  | 'REJECTED'
  | 'REQUESTED_FORWARD'
  | 'REQUESTED_SCAN'
  | 'RETURNED'
  | 'SCANNED'

export type MailActionType =
  | 'FORWARD'
  | 'SCAN'
  | 'RESEND'
  | 'DOWNLOAD'
  | 'MARK_AS_READ'
  | 'MARK_AS_UNREAD'

export type MailAction = {
  action_type: MailActionType
  requires_topup: boolean
  price?: number
}

export interface MailMetadata {
  document_title: string
  content: string
  reference_number: string
  summary: string
  industry: string
  date_of_issue: string
  categories: string[]
  sub_categories: string[]
  key_information: { 'key': string; 'value': string }[]
  sender_name: string
  sender?: {
    name: string
    logo?: { url: string }
  }
}

export interface Mail {
  date: string
  expiry: string
  read: boolean
  address: string | null
  metadata: MailMetadata | null

  id: string
  processed_by: string
  status: MailStatus
  display_status: string
  company_id: string
  actions: MailActionType[]
  company_product_subscription_id: string
}

export type GroupMember = {
  id: number
  user_id: number
  first_name: string
  last_name: string
  email: string
  role: string
  created_at: string
}
export type TeamMember = {
  id: number
  first_name: string
  toRemove?: boolean
  last_name: string
  email: string
  role: string
  created_at: string
  status: 0 | 1
}
export type APIKey = {
  id: string
  name: string
  created_on: string
  enabled: boolean
}
export interface VerifyAccount {
  id_token: string
  token: string
  refresh_token: string
  expires_in: number
  first_name: string
  last_name: string
  email: string
  partner_id: number
  partner_name: string
}

export type Invoice = {
  id: string
  raised_date: string
  invoice_number: string
  period: string
  total_billing: number
  total_due: number
}
export type LineItem = {
  created_at: string
  amount: number
  id: string
  plan_id: number
  subscription: CompanySubscriptionSummary
}
export type InvoiceDetail = {
  id: string
  raised_date: string
  invoice_number: string
  period: string
  total_billing: number
  total_due: number
  commission_rate?: number
  line_items: LineItem[]
}

export type PartnerSubscription = {
  id: number
  name: string
  previous_company_name: null
  trading_name: string
  contact_name: string
  contact_email: string
  company_id: number
  company_company_name: string
  plan: string
  status: string
  mail_notes: null
}

export interface PartnerAuditEvent {
  id: number
  event_type: string
  details: string
  actor: string
  created_at: string
}

export interface Auditor {
  id: number
  email: string
  name: string
}
export type KYCStatus = 'NotChecked' | 'CheckedPassed' | 'CheckedFailed'

export interface Ubo {
  id: number
  first_name: string
  middle_name: string
  last_name: string
  dob: string
  documents: Document[]
  'is_on_pep_list': KYCStatus
  'proof_of_address_status': KYCStatus
  'id_status': KYCStatus
  status: PartnerAuditStatus
}

export type PartnerAuditStatus =
  | 'CREATED'
  | 'SUBMITTED'
  | 'AWAITING_REVIEW'
  | 'COMPLETE'
  | 'IN_REVIEW'
  | 'ISSUE_REPORTED'
  | 'REQUESTED'
  | 'VERIFIED'
  | 'APPROVED'

export interface PartnerAuditCompany {
  id: number
  plan: string
  company_id: number
  company_name: string
  contact_name: string
  contact_email: string
  contact_telephone: string
  previous_company_name: string
  trading_name: string
  status: PartnerAuditStatus
  last_audited_at: string
  ubos: Ubo[]
}

export interface PartnerAudit {
  id: number
  partner_id: number
  partner_name: string
  events: PartnerAuditEvent[]
  companies: PartnerSubscription[]
  status: PartnerAuditStatus
  auditor: Auditor
  contact: { name: string; id: number; email: string }
  concluded_at: string
  created_at: string
  updated_at: string
}

export interface PartnerAuditDetail {
  id: number
  partner_id: number
  partner_name: string
  events: PartnerAuditEvent[]
  companies: PartnerAuditCompany[]
  status: string
  auditor: Auditor
  contact: { name: string; id: number; email: string }
  concluded_at: string
  created_at: string
  updated_at: string
}

export type InvoiceDownload = {
  url: string
}
export type Res = {
  confirmEmail: ID
  forgotPassword: ID
  resendConfirmationEmail: {}
  resetPassword: {}
  setPassword: {}
  codepushProgress: CodePushProgress
  user: {
    id: number
    email: string
    tenant_id: number
    first_name: string
    last_name: string
    handles_kyc: boolean
    role: number
    partner_id: number
    partner_name: string
    products: PartnerProduct[]
    settings: {
      can_view_mail: boolean
      can_download_mail: boolean
      has_access_to_scans_in_the_mobile_application: boolean
    }
  }
  redirect: Url
  subscriptions: PagedResponse<CompanySubscriptionSummary>
  stats: Stats
  products: PartnerProduct[]
  subscription: CompanySubscriptionDetail
  company: PagedResponse<CompanySubscriptionDetail>
  teamMembers: PagedResponse<TeamMember>
  roles: RoleSummary[]
  teamMember: TeamMember
  apiKeys: PagedResponse<APIKey>
  apiKey: { id: string }
  cancelSubscription: { id: string }
  invoices: PagedResponse<Invoice>
  invoice: InvoiceDetail
  invoiceDownload: InvoiceDownload

  devSettings: {
    enabled: boolean
  }
  resendInvite: { id: string }
  ubo: Member
  ubos: Member[]
  ubosVerif: MemberVeriff[]
  uboDocument: { id: string }
  mail: PagedResponse<Mail>
  confirmAccount: { id: string }
  password: { id: string }
  mailDownload: {
    urls: string[]
  }
  resendMailItem: { id: string }
  reactivateSubscription: { id: string }
  forwardMailItem: { id: string }
  groups: PagedResponse<Group>
  group: Group
  audits: PagedResponse<PartnerAudit>
  audit: PartnerAuditDetail
  auditDocumentUpload: {}
  auditAction: { id: string }
  assignCompanyGroups: {}
  assignAllCompanyGroups: {}
  companyGroup: { id: string }
  sendReminder: { id: string }
  uboVeriff: { id: string }
  uboVeriffs: {}
  // END OF TYPES
}
